import React, { useEffect } from 'react';
import { getContext } from '@fiverr-private/fiverr_context';
import {
    type BlockedActionType,
    type SigningModeType,
    type Variant,
    SIGNING_FLOW_TYPE_MAP,
    type AfterSigningSuccess,
    IdentificationContextProvider,
    useOnceBeforeRenderOnClient,
    type MixpanelEventsContext,
    type BigQueryEventsContext,
} from '@fiverr-private/identification_core';
import { globalBigQueryReporter, globalGrafanaReporter, globalMixpanelReporter } from '../../events';
import { MIXPANEL_SIGNING_MODE_TYPE_MAP } from '../../events/mixpanel/mixpanel.constants';
import { extractEmailDomain } from '../../shared/utils/extractEmailDomain';
import { StepsContextProvider } from '../StepsContext';
import { DEFAULT_STEP_ID, StepId } from '../../shared/constants/stepId';
import { IdentificationContent } from '../IdentificationContent';
import { ClientExperimentsContextProvider } from '../ClientExperimentsContext';
import { useWnwRedirect, useIllustrationExperiment } from './hooks';

interface IdentificationFormProps {
    mode?: SigningModeType;
    variant?: Variant;
    isModal?: boolean;
    isModalOpen?: boolean;
    isSticky?: boolean;
    isSocialFlow?: boolean;
    closeModal?: () => void;
    skipQuestionnaire?: boolean;
    skipActivation?: boolean;
    isCodeActivationSkippable?: boolean;
    redirectUrl?: string;
    email?: string;
    username?: string;
    blockedActionType?: BlockedActionType;
    stepId?: StepId;
    mixpanelEnrichment?: MixpanelEventsContext;
    bigQueryEnrichment?: BigQueryEventsContext;
    afterSigningSuccess?: AfterSigningSuccess;
    inLynxesIdentificationResetPasswordRollout?: boolean;
    inLynxesIdentificationIllustrationRollout?: boolean;
    inLynxesIdentificationResetPasswordFlowMixpanelRecordingsRollout?: boolean;
}

export const IdentificationForm = ({
    redirectUrl = '',
    mode = 'sign_up',
    variant = 'default',
    isModal = false,
    isModalOpen = false,
    isSticky = false,
    skipQuestionnaire = false,
    skipActivation = false,
    isCodeActivationSkippable = true,
    closeModal,
    isSocialFlow = false,
    blockedActionType,
    stepId = DEFAULT_STEP_ID,
    email = '',
    username = '',
    mixpanelEnrichment = {},
    bigQueryEnrichment = {},
    afterSigningSuccess,
    inLynxesIdentificationResetPasswordRollout = false,
    inLynxesIdentificationIllustrationRollout = false,
    inLynxesIdentificationResetPasswordFlowMixpanelRecordingsRollout = false,
}: IdentificationFormProps) => {
    const { url: currentUrl } = getContext();
    const wnwAfterSigningSuccess = useWnwRedirect({ variant });

    const { inLynxesIdentificationIllustrationExperimentGroupB } = useIllustrationExperiment({
        inLynxesIdentificationIllustrationRollout,
        isModal,
        isModalOpen,
    });

    useOnceBeforeRenderOnClient(() => {
        globalMixpanelReporter.enrichContext(
            {
                funnel: 'standard',
                provider: 'Standard',
                triggerType: 'User Trigger',
                experience: 'Identification',
                flow: mode === 'sign_up' ? 'Sign Up' : 'Sign In',
                mode: MIXPANEL_SIGNING_MODE_TYPE_MAP[mode],
                emailDomain: extractEmailDomain(email),
                currentUrl,
                ...mixpanelEnrichment,
            },
            { resetState: true }
        );
        globalBigQueryReporter.enrichContext(
            {
                mode,
                funnel: 'standard',
                provider: 'standard',
                reason: 'user_trigger',
                experience: 'Identification',
                flow: mode === 'sign_up' ? SIGNING_FLOW_TYPE_MAP.signUp : SIGNING_FLOW_TYPE_MAP.signIn,
                emailDomain: extractEmailDomain(email),
                ...bigQueryEnrichment,
            },
            { resetState: true }
        );
    });

    useEffect(() => {
        globalMixpanelReporter.send('Identification - Show');
        globalBigQueryReporter.send('identification.show');
        globalGrafanaReporter.count('identification.%{platform}.show');
    }, []);

    return (
        <ClientExperimentsContextProvider
            inLynxesIdentificationIllustrationExperimentGroupB={inLynxesIdentificationIllustrationExperimentGroupB}
        >
            <IdentificationContextProvider
                variant={variant}
                isSticky={isSticky}
                isSocialFlow={isSocialFlow}
                isModal={isModal}
                isModalOpen={isModalOpen}
                closeModal={closeModal}
                skipQuestionnaire={skipQuestionnaire}
                isCodeActivationSkippable={isCodeActivationSkippable}
                skipActivation={skipActivation}
                initialMode={mode}
                redirectUrl={redirectUrl}
                initialEmail={email}
                initialUsername={username}
                blockedActionType={blockedActionType}
                afterSigningSuccess={wnwAfterSigningSuccess || afterSigningSuccess}
                inLynxesIdentificationResetPasswordRollout={inLynxesIdentificationResetPasswordRollout}
                inLynxesIdentificationResetPasswordFlowMixpanelRecordingsRollout={
                    inLynxesIdentificationResetPasswordFlowMixpanelRecordingsRollout
                }
            >
                <StepsContextProvider stepId={stepId}>
                    <IdentificationContent />
                </StepsContextProvider>
            </IdentificationContextProvider>
        </ClientExperimentsContextProvider>
    );
};
